<template>
  <!-- inline: "0.80 USDC ≈ $0.80 ≈ A$1.12 · 115.00 USDT ≈ ..." (badges) -->
  <span v-if="inline && tokens.length">{{ inlineText }}</span>

  <!-- rows: amount left, fiat right — for boxes/cards -->
  <div v-else-if="rows && tokens.length">
    <div
      v-for="token in tokens"
      :key="token.currency"
      class="token-row d-flex justify-content-between align-items-baseline"
    >
      <span class="token-amount">{{ amountText(token) }}</span>
      <span class="token-fiat">{{ rowFiatText(token) }}</span>
    </div>
    <div
      v-if="showTotal"
      class="token-row token-total d-flex justify-content-between align-items-baseline"
    >
      <span class="token-amount">Total</span>
      <span class="token-fiat">{{ totalText }}</span>
    </div>
  </div>

  <!-- default: one text line per token -->
  <div v-else-if="tokens.length">
    <p v-for="token in tokens" :key="token.currency" class="mb-0">
      {{ tokenText(token) }}
    </p>
    <p v-if="showTotal" class="mb-0 font-weight-bold">
      Total ≈ {{ totalText }}
    </p>
  </div>

  <span v-else>{{ formatCurrencyBuiltInFunc(fallbackAmount || 0) }}</span>
</template>

<script>
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import { getTokenPrices } from "@/helpers/tokenPrices";
import {
  getToken,
  TOKEN_DEFAULT_DECIMALS_FORMAT,
} from "@core/utils/enum/token";

export default {
  name: "TokenBalances",
  props: {
    balances: { type: Array, default: () => [] },
    fallbackAmount: { type: Number, default: 0 },
    inline: { type: Boolean, default: false },
    rows: { type: Boolean, default: false },
    // append a Total (USD ≈ AUD) line summing all tokens
    showTotal: { type: Boolean, default: false },
  },
  data() {
    return { prices: {} };
  },
  computed: {
    inlineText() {
      const text = this.tokens.map((t) => this.tokenText(t)).join(" · ");
      return this.showTotal ? `${text} · Total ≈ ${this.totalText}` : text;
    },
    totalText() {
      const totalUsd = this.tokens.reduce((sum, t) => sum + t.usdValue, 0);
      const allAud = this.tokens.every((t) => t.audValue !== null);
      const totalAud = allAud
        ? this.tokens.reduce((sum, t) => sum + t.audValue, 0)
        : null;
      const usd = formatCurrencyBuiltInFunc(totalUsd);
      const aud =
        totalAud !== null
          ? ` ≈ ${Number(totalAud).toLocaleString("en-US", {
              style: "currency",
              currency: "AUD",
            })}`
          : "";
      return `${usd}${aud}`;
    },
    tokens() {
      return this.balances
        .filter((b) => Number(b.balance) > 0)
        .map((b) => {
          const amount = Number(b.balance) || 0;
          const price = this.prices[String(b.currency).toUpperCase()];
          return {
            currency: b.currency,
            amount,
            // CoinGecko USD price preferred; DWallet rate as fallback
            usdValue: amount * (price?.usd || Number(b.rate) || 0),
            audValue: price ? amount * price.aud : null,
          };
        });
    },
  },
  watch: {
    balances: {
      immediate: true,
      async handler(val) {
        if (val?.length) {
          this.prices = await getTokenPrices(val.map((b) => b.currency));
        }
      },
    },
  },
  methods: {
    // display decimals per token from @core/utils/enum/token (stable 2, others 4)
    amountText(token) {
      const decimalsFormat =
        getToken(token.currency)?.decimalsFormat ??
        TOKEN_DEFAULT_DECIMALS_FORMAT;
      const amount = Number(token.amount || 0).toLocaleString("en-US", {
        minimumFractionDigits: decimalsFormat,
        maximumFractionDigits: decimalsFormat,
      });
      return `${amount} ${token.currency}`;
    },
    audText(token) {
      return token.audValue !== null
        ? Number(token.audValue).toLocaleString("en-US", {
            style: "currency",
            currency: "AUD",
          })
        : "";
    },
    tokenText(token) {
      const usd = formatCurrencyBuiltInFunc(token.usdValue);
      const aud = this.audText(token);
      return `${this.amountText(token)} ≈ ${usd}${aud ? ` ≈ ${aud}` : ""}`;
    },
    rowFiatText(token) {
      const usd = formatCurrencyBuiltInFunc(token.usdValue);
      const aud = this.audText(token);
      return `${usd}${aud ? ` ≈ ${aud}` : ""}`;
    },
  },
  setup() {
    return { formatCurrencyBuiltInFunc };
  },
};
</script>
